import React, {useCallback, useEffect, useMemo} from 'react';
import {Link, useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {
  adminTranscriptionQueueApiSelector,
  adminTranscriptionQueuePageSelector,
} from '../store/selectors/admin.selectors';
import {fetchAdminTranscriptionQueue} from '../store/slices/adminThunks';
import {
  ADMIN_QUEUE_PAGE_SIZE,
  AdminSortOrder,
  AdminTranscriptionStatus,
} from '../domain/admin';
import {DeletedBadge, TranscriptionStatusBadge} from './admin/AdminBadges';
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Pagination,
  Panel,
  Timestamp,
  formatSeconds,
} from './admin/AdminPrimitives';
import {RefreshIcon} from './Icons';

/**
 * The global transcription queue — every job, regardless of whose it is.
 *
 * **Oldest first**, which is backwards from every other list in this panel and deliberately so:
 * newest-first is right when you are reading history, but in a set of jobs that are *still
 * running*, the oldest is the one that is stuck. The endpoint defaults to `asc` for the same
 * reason; this screen just does not override it.
 *
 * Defaults to `processing` because that is the question — a finished job is not a queue.
 */

const STATUSES: Array<{key: AdminTranscriptionStatus | 'all'; label: string; hint: string}> = [
  {key: 'processing', label: 'Running', hint: 'In flight right now. Oldest first — that is the stuck one.'},
  {key: 'failed', label: 'Failed', hint: 'The job errored.'},
  {
    key: 'rejected',
    label: 'Rejected',
    hint: 'Duration was under-reported; transcript withheld, seconds still charged.',
  },
  {key: 'completed', label: 'Completed', hint: 'Finished normally.'},
  {key: 'all', label: 'All', hint: 'Every transcription.'},
];

const isQueueStatus = (value: string | null): value is AdminTranscriptionStatus =>
  value === 'processing' || value === 'completed' || value === 'failed' || value === 'rejected';

const headerCellClass = 'px-4 py-2 font-medium';
const cellClass = 'px-4 py-2.5 align-top';

const AdminQueueContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = useAppSelector(adminTranscriptionQueuePageSelector);
  const api = useAppSelector(adminTranscriptionQueueApiSelector);

  const statusParam = searchParams.get('status');
  const status: AdminTranscriptionStatus | 'all' = statusParam === 'all'
    ? 'all'
    : isQueueStatus(statusParam) ? statusParam : 'processing';
  // Oldest first is the useful default for a running queue; the toggle exists because it stops
  // being useful the moment you switch to `completed`.
  const order: AdminSortOrder = searchParams.get('order') === 'desc' ? 'desc' : 'asc';
  const offset = Math.max(0, Number(searchParams.get('offset') ?? 0) || 0);

  const query = useMemo(
    () => ({
      status: status === 'all' ? undefined : status,
      order,
      limit: ADMIN_QUEUE_PAGE_SIZE,
      offset,
    }),
    [status, order, offset]
  );

  useEffect(() => {
    dispatch(fetchAdminTranscriptionQueue(query));
  }, [dispatch, query]);

  const setParams = useCallback(
    (patch: Record<string, string | undefined>, options?: {keepOffset?: boolean}) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          Object.entries(patch).forEach(([key, value]) => {
            if (value === undefined) {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });
          if (!options?.keepOffset) {
            next.delete('offset');
          }
          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  const rows = page?.items ?? [];

  return (
    <Panel
      padded={false}
      title="Transcription queue"
      subtitle="Every job across all users. Running jobs are listed oldest first — the oldest is the one that is stuck."
      actions={
        <button
          type="button"
          onClick={() => dispatch(fetchAdminTranscriptionQueue(query))}
          disabled={api.status === 'pending'}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-1
           text-[11px] font-medium text-gray-300 transition-colors hover:border-gray-600
           hover:bg-gray-800 disabled:opacity-40 cursor-pointer"
        >
          <RefreshIcon className={`h-3 w-3 ${api.status === 'pending' ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      }
    >
      <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-800 px-4 py-3">
        {STATUSES.map(entry => (
          <button
            key={entry.key}
            type="button"
            title={entry.hint}
            onClick={() => setParams({status: entry.key})}
            className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer ${
              status === entry.key
                ? 'bg-gray-800 text-white'
                : 'text-gray-400 hover:bg-gray-800/60 hover:text-gray-100'
            }`}
          >
            {entry.label}
          </button>
        ))}

        <button
          type="button"
          onClick={() => setParams({order: order === 'asc' ? 'desc' : 'asc'})}
          className="rounded-lg border border-gray-800 px-2.5 py-1 text-xs text-gray-300
           transition-colors hover:border-gray-700 hover:bg-gray-800/60 cursor-pointer"
        >
          {order === 'asc' ? 'Oldest first' : 'Newest first'}
        </button>

        <span className="ml-auto text-[11px] tabular-nums text-gray-600">
          {api.status === 'pending' ? 'Loading…' : `${(page?.total ?? 0).toLocaleString()} matching`}
        </span>
      </div>

      {api.status === 'rejected' && rows.length === 0 && (
        <ErrorState
          message={api.error ?? 'The queue could not be loaded.'}
          onRetry={() => dispatch(fetchAdminTranscriptionQueue(query))}
        />
      )}

      {api.status === 'pending' && rows.length === 0 && <LoadingState message="Loading the queue…" />}

      {api.status !== 'pending' && api.status !== 'rejected' && rows.length === 0 && (
        <EmptyState
          message={status === 'processing' ? 'Nothing is running.' : 'No transcriptions match.'}
          hint={status === 'processing' ? 'No job is currently in flight.' : undefined}
        />
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-xs">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-950/40 text-[11px] uppercase tracking-wide text-gray-500">
                <th scope="col" className={headerCellClass}>Started</th>
                <th scope="col" className={headerCellClass}>File</th>
                <th scope="col" className={headerCellClass}>Owner</th>
                <th scope="col" className={headerCellClass}>Status</th>
                <th scope="col" className={`${headerCellClass} text-right`}>Duration</th>
                <th scope="col" className={headerCellClass}>Updated</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-800/60">
              {rows.map(row => (
                <tr key={row.id} className="transition-colors hover:bg-gray-900/60">
                  <td className={`${cellClass} text-gray-400`}>
                    <Timestamp iso={row.createdAt} />
                  </td>

                  <td className={`${cellClass} max-w-[280px]`}>
                    <span
                      className={`block truncate text-gray-100 ${row.isDeleted ? 'line-through' : ''}`}
                      title={row.name}
                    >
                      {row.name}
                    </span>
                    {row.audioFilename && (
                      <span className="block truncate text-[11px] text-gray-500">
                        {row.audioFilename}
                      </span>
                    )}
                    {row.isDeleted && (
                      <span className="mt-1 inline-block">
                        <DeletedBadge />
                      </span>
                    )}
                    {row.error && <p className="mt-1 text-[11px] text-red-300">{row.error}</p>}
                  </td>

                  <td className={`${cellClass} max-w-[220px]`}>
                    {row.user ? (
                      <Link
                        to={`/admin/users/${row.user.id}`}
                        className="block truncate text-indigo-400 hover:text-indigo-300"
                        title={row.user.email}
                      >
                        {row.user.name || row.user.email}
                      </Link>
                    ) : (
                      <span className="text-gray-600">—</span>
                    )}
                  </td>

                  <td className={cellClass}>
                    <TranscriptionStatusBadge status={row.status} />
                  </td>

                  <td className={`${cellClass} text-right tabular-nums text-gray-300`}>
                    {formatSeconds(row.durationSeconds)}
                  </td>

                  <td className={`${cellClass} text-gray-400`}>
                    <Timestamp iso={row.updatedAt} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pagination
        total={page?.total ?? 0}
        limit={page?.limit || ADMIN_QUEUE_PAGE_SIZE}
        offset={offset}
        onOffsetChange={next => setParams({offset: String(next)}, {keepOffset: true})}
        noun="transcriptions"
        isLoading={api.status === 'pending'}
      />
    </Panel>
  );
};

export default AdminQueueContainer;
